import React from "react";
import { Sheet, Chip, Tooltip, Box } from "@mui/joy";
import DarkInput from "../../common/DarkInput";
import type {
  EquipmentModel,
  AttributeType,
  EquipmentAttributeDefinition,
} from "../../../api/equipment/Equipment.typed";
import {
  SEdit,
  SDelete,
  SDone,
  SClose,
  DriveFileRenameOutlineIcon,
  AddAlertIcon,
} from "../../../styles/StyledMUI";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";

interface EquipmentAttributesListProps {
  model: EquipmentModel;
  onModelChange: (model: EquipmentModel) => void;
  onOpenOptions: (idx: number) => void;
  onOpenAlert: (idx: number) => void;
}

export default function EquipmentAttributesList({
  model,
  onModelChange,
  onOpenOptions,
  onOpenAlert,
}: EquipmentAttributesListProps) {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EQUIPMENT_EDIT);
  const [editingAttrIdx, setEditingAttrIdx] = React.useState<number | null>(null);
  const [editingAttrName, setEditingAttrName] = React.useState("");
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  const typeLabel: Record<AttributeType, string> = {
    string: "Texte",
    number: "Nombre",
    date: "Date",
    list: "Liste",
    comments: "Commentaires",
  };

  const removeAttribute = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedAttrs = [...(model.Attributes || [])].filter((_, i: number) => i !== index);
    onModelChange({ ...model, Attributes: updatedAttrs });
  };

  const startEditAttributeName = (idx: number) => {
    setEditingAttrIdx(idx);
    setEditingAttrName((model.Attributes || [])[idx]?.Label || "");
  };

  const saveAttributeName = () => {
    if (editingAttrIdx === null || !editingAttrName.trim()) return;
    const attrs = [...(model.Attributes || [])];
    attrs[editingAttrIdx] = { ...attrs[editingAttrIdx], Label: editingAttrName.trim() };
    onModelChange({ ...model, Attributes: attrs });
    setEditingAttrIdx(null);
    setEditingAttrName("");
  };

  const cancelEditAttributeName = () => {
    setEditingAttrIdx(null);
    setEditingAttrName("");
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null) return;

    if (draggedIndex !== dropIndex) {
      const newAttrs = [...(model.Attributes || [])];
      const [movedItem] = newAttrs.splice(draggedIndex, 1);
      newAttrs.splice(dropIndex, 0, movedItem);
      onModelChange({ ...model, Attributes: newAttrs });
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <>
      {(model.Attributes || []).map((a: EquipmentAttributeDefinition, idx: number) => (
        <Sheet
          key={a.ID}
          variant="soft"
          draggable={editingAttrIdx !== idx}
          onDragStart={(e) => handleDragStart(e, idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, idx)}
          onDragEnd={handleDragEnd}
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 2,
            backgroundColor: colorTokens.bg.surfaceMuted,
            cursor: editingAttrIdx === idx ? "default" : "grab",
            opacity: draggedIndex === idx ? 0.5 : 1,
            transform: dragOverIndex === idx && draggedIndex !== idx ? "scale(1.02)" : "scale(1)",
            transition: "all 0.15s ease",
            borderColor:
              dragOverIndex === idx && draggedIndex !== idx ? "primary.500" : "transparent",
            borderWidth: 2,
            borderStyle: "solid",
            "&:hover": {
              backgroundColor:
                editingAttrIdx === idx
                  ? colorTokens.bg.surfaceMuted
                  : colorTokens.bg.surfaceElevated,
            },
            "&:active": {
              cursor: editingAttrIdx === idx ? "default" : "grabbing",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 1,
              color: colorTokens.text.muted,
              cursor: editingAttrIdx === idx ? "default" : "grab",
              "&:hover": {
                color: editingAttrIdx === idx ? colorTokens.text.muted : colorTokens.accent.primary,
              },
            }}
          >
            <Tooltip title={editingAttrIdx === idx ? "" : "Glisser pour réorganiser"}>
              <Box sx={{ fontSize: "14px", lineHeight: 1 }}>⋮⋮</Box>
            </Tooltip>
          </Box>

          {editingAttrIdx === idx ? (
            <DarkInput
              value={editingAttrName}
              onChange={(e) => setEditingAttrName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveAttributeName();
                if (e.key === "Escape") cancelEditAttributeName();
              }}
              sx={{ flexGrow: 1 }}
              autoFocus
            />
          ) : (
            <DarkTypography
              sx={{
                flexGrow: 1,
                cursor: "pointer",
                color: colorTokens.text.primary,
              }}
              onClick={() => startEditAttributeName(idx)}
            >
              {a.Label}
            </DarkTypography>
          )}
          <Chip
            size="sm"
            variant="soft"
            color="neutral"
            sx={{
              backgroundColor: colorTokens.bg.surface,
              color: colorTokens.text.primary,
            }}
          >
            {typeLabel[a.Type as AttributeType]}
          </Chip>

          {editingAttrIdx === idx ? (
            <>
              <Tooltip title="Sauvegarder le nom">
                <IconButtonCTA
                  ctaVariant="success"
                  size="sm"
                  variant="plain"
                  aria-label="Sauvegarder nom attribut"
                  onClick={saveAttributeName}
                  sx={{
                    color: colorTokens.status.success,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
                  }}
                >
                  <SDone />
                </IconButtonCTA>
              </Tooltip>
              <Tooltip title="Annuler l'édition">
                <IconButtonCTA
                  ctaVariant="danger"
                  size="sm"
                  variant="plain"
                  aria-label="Annuler édition nom attribut"
                  onClick={cancelEditAttributeName}
                  sx={{
                    color: colorTokens.status.danger,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
                  }}
                >
                  <SClose />
                </IconButtonCTA>
              </Tooltip>
            </>
          ) : (
            <>
              {canManage && (
                <Tooltip title="Renommer l'attribut">
                  <IconButtonCTA
                    ctaVariant="neutral"
                    size="sm"
                    variant="plain"
                    aria-label="Renommer attribut"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditAttributeName(idx);
                    }}
                    sx={{
                      color: colorTokens.text.secondary,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.text.secondary, 0.1) },
                    }}
                  >
                    <DriveFileRenameOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButtonCTA>
                </Tooltip>
              )}
              {a.Type === "date" && canManage && (
                <Tooltip title="Alertes">
                  <IconButtonCTA
                    ctaVariant="accent"
                    size="sm"
                    variant="plain"
                    aria-label="Configurer alertes attribut date"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenAlert(idx);
                    }}
                    sx={{
                      color: colorTokens.accent.primary,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.accent.primary, 0.1) },
                    }}
                  >
                    <AddAlertIcon sx={{ fontSize: 18 }} />
                  </IconButtonCTA>
                </Tooltip>
              )}
              {a.Type === "list" && canManage && (
                <Tooltip title="Options de la liste">
                  <IconButtonCTA
                    ctaVariant="neutral"
                    size="sm"
                    variant="plain"
                    aria-label="Configurer options attribut liste"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenOptions(idx);
                    }}
                    sx={{
                      color: colorTokens.text.secondary,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.text.secondary, 0.1) },
                    }}
                  >
                    <SEdit />
                  </IconButtonCTA>
                </Tooltip>
              )}
              {canManage && (
                <IconButtonCTA
                  ctaVariant="danger"
                  size="sm"
                  variant="plain"
                  aria-label="Supprimer attribut"
                  onClick={(e) => removeAttribute(idx, e)}
                  sx={{
                    color: colorTokens.status.danger,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
                  }}
                >
                  <SDelete />
                </IconButtonCTA>
              )}
            </>
          )}
        </Sheet>
      ))}
    </>
  );
}
